import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CalendarClockIcon, PlusIcon, VideoIcon, UserPlusIcon, RepeatIcon, ScissorsIcon } from 'lucide-react';
import { PageHeader } from '../components/layout/PageHeader';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { StatCard } from '../components/ui/StatCard';
import { appointments, type Appointment } from '../data/operations';
const statusTone = {
  Scheduled: 'neutral',
  'Checked-in': 'info',
  'In consult': 'brand',
  Completed: 'success',
  'No-show': 'danger'
} as const;
const typeMeta = {
  New: {
    icon: UserPlusIcon,
    tone: 'brand' as const
  },
  'Follow-up': {
    icon: RepeatIcon,
    tone: 'info' as const
  },
  'Video consultation': {
    icon: VideoIcon,
    tone: 'purple' as const
  },
  Procedure: {
    icon: ScissorsIcon,
    tone: 'warning' as const
  }
};
const hours = ['09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '12:00'];
function Row({
  appt
}: {
  appt: Appointment;
}) {
  const meta = typeMeta[appt.type];
  return <motion.div initial={{
    opacity: 0,
    x: -6
  }} animate={{
    opacity: 1,
    x: 0
  }} className="flex items-center gap-3 rounded-lg border border-slate-100 bg-white px-3 py-2.5 hover:border-brand-200 hover:shadow-card transition-all" data-id="element-520" data-mp="cops3010xm1r">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600" data-id="element-521" data-mp="3l5udji0jebbi">
        <meta.icon className="h-4 w-4" data-id="element-522" data-mp="3p7xom5jandw9" />
      </div>
      <div className="min-w-0 flex-1" data-id="element-523" data-mp="3p8r0fkx40tl9">
        <p className="text-sm font-medium text-slate-900 truncate" data-id="element-524" data-mp="3b7wo4r6xy8hb">{appt.patient}</p>
        <p className="text-xs text-slate-500" data-id="element-525" data-mp="3g6q36o1vm7jl">{appt.mrn} · {appt.doctor}</p>
      </div>
      <Badge tone={meta.tone} data-id="element-526" data-mp="3g8kow4kz23oq">{appt.type}</Badge>
      <Badge tone={statusTone[appt.status]} data-id="element-527" data-mp="19hbgrii4s8e6">{appt.status}</Badge>
    </motion.div>;
}
export function Appointments() {
  const navigate = useNavigate();
  return <div className="p-6" data-id="element-528" data-mp="1cy7lyrrjgh8k">
      <PageHeader title="Appointments & Scheduling" subtitle="Multi-channel booking · walk-in tokens · video consultation · resource scheduling" action={<>
            <Button variant="outline" size="sm" data-id="element-530" data-mp="12z8az2jaw9ie">Today</Button>
            <Button icon={<PlusIcon className="h-4 w-4" data-id="element-532" data-mp="y0yhqxh4zizf" />} onClick={() => navigate('/appointments/new')} data-id="element-531" data-mp="131us9tvzu3dt">Book appointment</Button>
          </>} data-id="element-529" data-mp="1cyhbyfcl2oe9" />

      <div className="mb-5 grid grid-cols-2 gap-4 lg:grid-cols-4" data-id="element-533" data-mp="1vndxnutx08kv">
        <StatCard label="Booked today" value="86" icon={<CalendarClockIcon className="h-5 w-5" data-id="element-535" data-mp="1qqe0yaiygi6f" />} accent="text-brand-600 bg-brand-50" footnote="Across 14 clinics" data-id="element-534" data-mp="1qp2nfizpjw24" />
        <StatCard label="Checked-in" value="41" icon={<UserPlusIcon className="h-5 w-5" data-id="element-537" data-mp="1h0kpd9itc326" />} accent="text-sky-600 bg-sky-50" footnote="Now in queue" data-id="element-536" data-mp="1lwj92jt74ww9" />
        <StatCard label="No-show rate" value="6.2%" delta={{
        value: '1.1%',
        direction: 'down',
        good: true
      }} icon={<RepeatIcon className="h-5 w-5" data-id="element-539" data-mp="29nf5xvcieiad" />} accent="text-emerald-600 bg-emerald-50" data-id="element-538" data-mp="2eg8cmorjg0p3" />
        <StatCard label="Video consultations" value="12" icon={<VideoIcon className="h-5 w-5" data-id="element-541" data-mp="2hzgx23fbk5su" />} accent="text-violet-600 bg-violet-50" footnote="Scheduled video sessions" data-id="element-540" data-mp="2hxw4ukgh8yy5" />
      </div>

      <div className="grid gap-4 lg:grid-cols-3" data-id="element-542" data-mp="2qx9f3fcfdrjx">
        {/* Day grid */}
        <Card className="lg:col-span-2 p-4" data-id="element-543" data-mp="2vwvr5lgip1r5">
          <div className="mb-3 flex items-center justify-between" data-id="element-544" data-mp="2vxx09kdz3e68">
            <h3 className="text-sm font-semibold text-slate-900" data-id="element-545" data-mp="30w8ahw86jqoz">Dr. Imran Sethi · Cardiology · Today</h3>
            <span className="text-xs text-slate-500" data-id="element-546" data-mp="34x1zksxczbjg">30-min slots</span>
          </div>
          <div className="space-y-2" data-id="element-547" data-mp="39ok1pkb4bi60">
            {hours.map(h => {
            const appt = appointments.find(a => a.time === h);
            return <div key={h} className="flex items-stretch gap-3" data-id="element-548" data-mp="3emli1xh5t3ds">
                  <span className="w-14 shrink-0 pt-2.5 text-right text-xs font-medium text-slate-400 tabular-nums" data-id="element-549" data-mp="3jnizu8y9bver">{h}</span>
                  <div className="flex-1" data-id="element-550" data-mp="3olsxqrbd9a42">
                    {appt ? <Row appt={appt} data-id="element-551" data-mp="p6igh1szsg3c" /> : <button className="flex h-[52px] w-full items-center justify-center rounded-lg border border-dashed border-slate-200 text-xs text-slate-400 hover:border-brand-300 hover:text-brand-600 transition-colors" data-id="element-552" data-mp="k8qk9vlbe7tc">
                        <PlusIcon className="mr-1 h-3.5 w-3.5" data-id="element-553" data-mp="tzbkfngmzked" /> Available
                      </button>}
                  </div>
                </div>;
          })}
          </div>
        </Card>

        {/* Queue / waitlist */}
        <div className="space-y-4" data-id="element-554" data-mp="f9e1pn1h7mwk">
          <Card className="p-5" data-id="element-555" data-mp="fafb7n2ug1c6">
            <h3 className="mb-3 text-sm font-semibold text-slate-900" data-id="element-556" data-mp="f79nab5yb5i1">Live Token Queue</h3>
            <div className="space-y-2" data-id="element-557" data-mp="b6hchdhfksjn">
              {[{
              token: 'A-042',
              name: 'Ali Hamza',
              wait: '4 min',
              now: true
            }, {
              token: 'A-043',
              name: 'Sana Tariq',
              wait: '12 min'
            }, {
              token: 'A-044',
              name: 'Bilal Nawaz',
              wait: '19 min'
            }].map(t => <div key={t.token} className={`flex items-center gap-3 rounded-lg p-2.5 ${t.now ? 'bg-brand-50 ring-1 ring-brand-200' : 'bg-slate-50'}`} data-id="element-558" data-mp="1u8jj9d791i8m">
                  <span className={`rounded-md px-2 py-1 text-xs font-bold ${t.now ? 'bg-brand-600 text-white' : 'bg-white text-slate-600 ring-1 ring-slate-200'}`} data-id="element-559" data-mp="1k9c88wnzlzoo">{t.token}</span>
                  <div className="min-w-0 flex-1" data-id="element-560" data-mp="2cwev2rjawlz3">
                    <p className="text-sm font-medium text-slate-900 truncate" data-id="element-561" data-mp="2cxq5vbb5ytzp">{t.name}</p>
                    <p className="text-[11px] text-slate-500" data-id="element-562" data-mp="2hv9m2grf9gua">waiting {t.wait}</p>
                  </div>
                  {t.now && <Badge tone="brand" data-id="element-563" data-mp="2352ckqje785q">Now serving</Badge>}
                </div>)}
            </div>
          </Card>

          <Card className="p-5" data-id="element-564" data-mp="2vw2ij71aam01">
            <h3 className="mb-3 text-sm font-semibold text-slate-900" data-id="element-565" data-mp="2vv0um1z6650w">Reminders sent</h3>
            <div className="space-y-2 text-sm" data-id="element-566" data-mp="30nkk9xb4sqm2">
              <div className="flex justify-between" data-id="element-567" data-mp="30odj8c3umbtn"><span className="text-slate-600" data-id="element-568" data-mp="3aj14wnlaaq7w">SMS</span><span className="font-semibold text-slate-900" data-id="element-569" data-mp="3ahq48wn4xyl0">64</span></div>
              <div className="flex justify-between" data-id="element-570" data-mp="35khi6kyv9aeh"><span className="text-slate-600" data-id="element-571" data-mp="35hbt35q8m8ir">WhatsApp</span><span className="font-semibold text-slate-900" data-id="element-572" data-mp="31h1p1ym09s6j">38</span></div>
              <div className="flex justify-between" data-id="element-573" data-mp="2wj075h0bfywn"><span className="text-slate-600" data-id="element-574" data-mp="2whyjmd245jy1">Email</span><span className="font-semibold text-slate-900" data-id="element-575" data-mp="2wl2tnr29f9wm">51</span></div>
            </div>
          </Card>
        </div>
      </div>
    </div>;
}